import { Button, Popover, Spin, Tooltip, Popconfirm } from "antd";
import React from "react";
import {
  isEmbedMsgStepId,
  TriggerCreate,
  TriggerForEditor,
  TriggerStepCreate,
} from "@dots-projects/types";
import { UseFormReturn } from "react-hook-form";
import { addSteps, deleteSteps, saveDraft } from "./reducer/actionCreators";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import { Input } from "./Input";
import { StepFormSendMessage } from "./StepFormSendMessage";
import { StepFormSendSurvey } from "./StepFormSendSurvey";
import { FormLabel } from "./FormLabel";
import { StepFormTestCaptcha } from "./StepFormTestCaptcha";
import { StepFormDeleteChannel } from "./StepFormDeleteChannel";
import { StepFormCreateChannel } from "./StepFormCreateChannel";
import { StepFormEditRoles } from "./StepFormEditRoles";
import { StepFormShowModal } from "./StepFormShowModal";
import { getPersonalizationVariablesByStep } from "../../../../../libs/types/src/lib/getAvailableVariables";
import { StepFormSendEmbedMessage } from "./StepFormSendEmbedMessage";
import { StepFormCreateThread } from "./StepFormCreateThread";
import { StepFormEditDotsTags } from "./StepFormEditDotsTags";
import {
  CloseCircleOutlined,
  CopyOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { StepFormZapier } from "./StepFormZapier";
import { StepFormChangeNickname } from "./StepFormChangeNickname";
import StepFormKickMember from "./StepFormKickMember";
import { copySteps } from "./stepTemplates";
import { StepFormSaveUserField } from "./StepSaveUserField";
import { StepFormFindUser } from "./StepFindUser";

/**
 * This component is used in components for specific trigger steps, such as StepFormSendSurvey.
 *
 * It includes common parts such as step type, name, delete and save.
 */
export const StepFormWithCommonParts: React.FC<{
  onSubmit: () => void;
  step: TriggerStepCreate;
  form: UseFormReturn<any>;
  customTitle?: string;
  customDescription?: any;
}> = ({ onSubmit, step, children, form, customTitle, customDescription }) => {
  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();

  const {
    register,
    formState: { isValid },
  } = form;

  const readOnly = !state.trigger.current.isDraft;
  const isPersisting = state.editor.isPersisting === "save-draft";
  const capitalize = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const stepTypesToConfirmBeforeDeleting = ["send_multiple_select"];

  const onSave = async () => {
    await onSubmit();
    dispatch({ type: "unselect" });
    window.analytics.track("Trigger - step saved");
  };

  const onDelete = () => {
    dispatch(deleteSteps([step.id]));
    dispatch({ type: "unselect" });
    dispatch(saveDraft(onPersist));
    window.analytics.track("Trigger - step deleted");
  };

  const onCopy = () => {
    const newCopySteps = copySteps(state.trigger.current, step);

    dispatch(addSteps(newCopySteps));
    dispatch({ type: "unselect" });
    dispatch(saveDraft(onPersist));
  };

  const modalTitle = () => {
    if (customTitle) {
      return customTitle;
    } else {
      return capitalize(step.type.replaceAll("_", " "));
    }
  };

  const submitButton = (
    <Button
      type="primary"
      htmlType="submit"
      disabled={isPersisting || !isValid}
      style={{ width: "100%" }}
    >
      <div>
        {isPersisting && <Spin />}
        {!isPersisting && !isValid && <WarningOutlined></WarningOutlined>}
        <span>Save Action</span>
      </div>
    </Button>
  );
  return (
    <form className="flex flex-col gap-3 text-left" onSubmit={onSave}>
      <div className="flex justify-between">
        <FormLabel label={modalTitle()}></FormLabel>
        <Tooltip title="Save and close" placement="bottom">
          <CloseCircleOutlined
            style={{ fontSize: "20px", color: "#6C6C6C" }}
            onClick={onSave}
          />
        </Tooltip>
      </div>
      {customDescription ? customDescription : null}
      <FormLabel label="Step name">
        <Input
          type={"text"}
          style={{ width: "100%" }}
          readOnly={readOnly}
          {...register("name")}
        />
      </FormLabel>
      {children}
      <Button
        onClick={onCopy}
        className="text-dots-vibrant-orange"
        style={{ color: "#FC845E" }}
        icon={<CopyOutlined />}
        type="link"
      >
        Duplicate step
      </Button>

      {!readOnly && (
        <>
          {stepTypesToConfirmBeforeDeleting.includes(step.type) ? (
            <Popconfirm
              title="Deleting this send survey will delete steps for its options too. Proceed?"
              onConfirm={onDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button>Delete step</Button>
            </Popconfirm>
          ) : (
            <Button onClick={onDelete}>Delete step</Button>
          )}

          {!isPersisting && !isValid ? (
            <Popover
              placement="bottom"
              overlayStyle={{ width: "220px" }}
              content={"Please fill in required fields."}
            >
              {submitButton}{" "}
            </Popover>
          ) : (
            <div>{submitButton}</div>
          )}
        </>
      )}
    </form>
  );
};

/**
 * Select corresponding StepFormXXX component based on the step type
 */
export function TriggerStepForm({
  step,
  trigger,
}: {
  step: TriggerStepCreate;
  trigger: TriggerForEditor | TriggerCreate;
}) {
  const stepVariables = getPersonalizationVariablesByStep(trigger, step);
  // Remove community id and platform variables from the list of available variables because them can cause confusion for end users.
  // remove messageId variables from the list of variables because it's irrelevant to the user
  const insertVariables = stepVariables.filter((v) => {
    return (
      !["communityId", "platform"].includes(v.id) &&
      !v.value.includes("messageId")
    );
  });
  const commonStepVariables = { step, insertVariables };

  switch (step.type) {
    case "create_channel":
      return <StepFormCreateChannel {...commonStepVariables} />;
    case "send_message":
      return isEmbedMsgStepId(step.id) ? (
        <StepFormSendEmbedMessage step={step} />
      ) : (
        <StepFormSendMessage {...commonStepVariables} />
      );
    case "send_survey":
    case "send_multiple_select":
      return <StepFormSendSurvey {...commonStepVariables} />;
    case "test_captcha":
      return <StepFormTestCaptcha {...commonStepVariables} />;
    case "delete_channel":
      return <StepFormDeleteChannel step={step} />;
    case "edit_roles":
      return <StepFormEditRoles {...commonStepVariables} />;
    case "edit_dots_tags":
      return <StepFormEditDotsTags {...commonStepVariables} />;
    case "create_thread":
      return <StepFormCreateThread step={step} />;
    case "open_modal":
      return <StepFormShowModal {...commonStepVariables} />;
    case "send_webhook":
      return <StepFormZapier {...commonStepVariables} />;
    case "kick_member":
      return <StepFormKickMember {...commonStepVariables} />;
    case "change_nickname":
      return <StepFormChangeNickname {...commonStepVariables} />;
    case "find_user":
      return <StepFormFindUser {...commonStepVariables} />;
    case "save_user_field":
      return <StepFormSaveUserField {...commonStepVariables} />;
    default:
      return <div>{step.type} Not yet supported!</div>;
  }
}
